<template>
  <div>
    <p v-if="failed" class="awb-note awb-note--warn" role="alert">{{ $t("airwallexBalance.loadFailed") }}</p>

    <p v-else-if="loaded && !summary.accounts.length" class="awb-empty">{{ $t("airwallexBalance.noAccount") }}</p>

    <div v-else-if="loaded" class="awb-layout">
      <div class="awb-layout__main">
        <div v-for="account in mismatched" :key="'m' + account.airwallex_account_id" class="awb-note awb-note--warn" role="status">
          {{ $t("airwallexBalance.reconcileMismatch", {
            ledger: money(account.reconciliation.ledger_available),
            airwallex: money(account.reconciliation.airwallex_available),
          }) }}
        </div>

        <div v-if="summary.uncredited.count" class="awb-note awb-note--warn d-flex flex-wrap justify-content-between align-items-center gap-2" role="status">
          <span>{{ $t("airwallexBalance.uncreditedBanner", { count: summary.uncredited.count, amount: money(summary.uncredited.amount) }) }}</span>
          <button type="button" class="awb-link" @click="$emit('show-transactions', null)">{{ $t("airwallexBalance.view") }}</button>
        </div>

        <div class="awb-kpis">
          <div v-for="kpi in kpis" :key="kpi.label" class="awb-card awb-kpi">
            <div class="awb-muted small mb-1">{{ $t(kpi.label) }}</div>
            <div class="awb-kpi__value">{{ kpi.value }}</div>
            <div class="small mt-1" :class="kpi.class">{{ kpi.hint }}</div>
          </div>
        </div>

        <section class="awb-card" aria-labelledby="awb-earn">
          <div class="d-flex flex-wrap justify-content-between gap-3 align-items-start mb-3">
            <div>
              <h2 id="awb-earn" class="awb-h2">{{ $t("airwallexBalance.netEarnings") }}</h2>
              <div class="awb-big">{{ money(summary.totals.net_earnings) }}</div>
              <div class="small awb-muted">
                <span v-if="netChange" :class="netChange.class" class="fw-bold">{{ netChange.text }}</span>
                {{ $t("airwallexBalance.netVsPrevious", {
                  from: dateShort(summary.previous.from),
                  to: dateShort(summary.previous.to),
                  amount: money(summary.previous.net_earnings),
                }) }}
              </div>
            </div>
            <div class="awb-segment" role="group" :aria-label="$t('airwallexBalance.period')">
              <button
                v-for="option in ['day', 'month']"
                :key="option"
                type="button"
                :aria-pressed="groupBy === option ? 'true' : 'false'"
                @click="setGroupBy(option)"
              >
                {{ $t("airwallexBalance." + option) }}
              </button>
            </div>
          </div>
          <apexchart type="bar" height="260" :options="chartOptions" :series="chartSeries" />
          <p class="small awb-muted mb-0 mt-2">{{ $t("airwallexBalance.legendNote") }}</p>
          <p v-if="summary.totals.unlinked_payment_count" class="awb-note awb-note--info mt-2">
            {{ $t("airwallexBalance.unlinked", { count: summary.totals.unlinked_payment_count }) }}
          </p>
        </section>

        <section class="awb-card" aria-labelledby="awb-recent">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h2 id="awb-recent" class="awb-h2">{{ $t("airwallexBalance.recentTransactions") }}</h2>
            <button type="button" class="awb-btn" @click="$emit('show-transactions', null)">{{ $t("airwallexBalance.seeAll") }}</button>
          </div>
          <p v-if="!recent.length" class="awb-empty mb-0">{{ $t("airwallexBalance.empty") }}</p>
          <div v-else class="awb-table-wrap">
            <table class="awb-table">
              <thead>
                <tr>
                  <th scope="col">{{ $t("airwallexBalance.colDate") }}</th>
                  <th scope="col">{{ $t("airwallexBalance.colType") }}</th>
                  <th scope="col">{{ $t("airwallexBalance.colOrder") }}</th>
                  <th scope="col">{{ $t("airwallexBalance.colStatus") }}</th>
                  <th scope="col" class="awb-right">{{ $t("airwallexBalance.colGross") }}</th>
                  <th scope="col" class="awb-right">{{ $t("airwallexBalance.colFee") }}</th>
                  <th scope="col" class="awb-right">{{ $t("airwallexBalance.colNet") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in recent" :key="row.id" class="awb-row" @click="$emit('open', row.id)">
                  <td class="text-nowrap">{{ dateTime(row.created_at) }}</td>
                  <td>{{ $t(typeKey[row.category]) }}</td>
                  <td>
                    <button type="button" class="awb-link" @click.stop="$emit('open', row.id)">{{ rowRef(row) }}</button>
                  </td>
                  <td><span class="awb-badge" :class="statusClass(row)">{{ $t(statusKey(row)) }}</span></td>
                  <td class="awb-right awb-num">{{ money(row.gross_amount) }}</td>
                  <td class="awb-right awb-num awb-muted">{{ money(row.merchant_fee) }}</td>
                  <td class="awb-right awb-num fw-bold">{{ money(row.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <aside class="awb-layout__side">
        <section v-if="balanceAccounts.length > 1" class="awb-card">
          <h2 class="awb-h2 mb-3">{{ $t("airwallexBalance.totalBalance") }}</h2>
          <div class="awb-big mb-3">{{ money(combined.total) }}</div>
          <dl class="awb-dl">
            <dt>{{ $t("airwallexBalance.available") }}</dt>
            <dd>{{ money(combined.available) }}</dd>
            <dt>{{ $t("airwallexBalance.pending") }}</dt>
            <dd>{{ money(combined.pending) }}</dd>
            <dt>{{ $t("airwallexBalance.estimatedAmount") }}</dt>
            <dd>{{ money(combined.nextPayout) }}</dd>
          </dl>
        </section>

        <section v-for="account in balanceAccounts" :key="'b' + account.airwallex_account_id" class="awb-card">
          <h2 class="awb-h2 mb-3">
            {{ $t("airwallexBalance.walletBalance") }}<span v-if="balanceAccounts.length > 1" class="awb-muted"> · {{ accountName(account) }}</span>
          </h2>
          <p v-if="!account.balance" class="awb-note awb-note--warn">{{ $t("airwallexBalance.balanceUnavailable") }}</p>
          <template v-else>
            <div class="awb-big mb-3">{{ money(account.balance.total) }}</div>
            <dl class="awb-dl">
              <dt>{{ $t("airwallexBalance.available") }}</dt>
              <dd>{{ money(account.balance.available) }}</dd>
              <dt>{{ $t("airwallexBalance.pending") }}</dt>
              <dd>{{ money(account.balance.pending) }}</dd>
              <dt>{{ $t("airwallexBalance.reserve") }}</dt>
              <dd>{{ money(account.next_payout.reserved_balance) }}</dd>
            </dl>
            <div class="border-top mt-3 pt-3 d-flex flex-column gap-2">
              <div class="d-flex justify-content-between gap-3 small">
                <span class="awb-muted">{{ $t("airwallexBalance.nextPayout") }}</span>
                <span class="fw-bold">{{ dateTime(account.next_payout.scheduled_at) }}</span>
              </div>
              <div class="d-flex justify-content-between gap-3 small">
                <span class="awb-muted">{{ $t("airwallexBalance.estimatedAmount") }}</span>
                <span class="fw-bold awb-num">{{ money(account.next_payout.amount) }}</span>
              </div>
              <p v-if="!Number(account.next_payout.amount)" class="awb-note awb-note--warn">
                {{ $t("airwallexBalance.noPayout", { amount: money(account.next_payout.reserved_balance) }) }}
              </p>
              <p class="small awb-muted mb-0">{{ $t("airwallexBalance.payoutNote") }}</p>
            </div>
          </template>
          <button type="button" class="awb-btn awb-btn--soft w-100 mt-3" @click="$emit('show-transactions', 'payout')">
            {{ $t("airwallexBalance.viewPayouts") }}
          </button>
        </section>

        <section class="awb-card" aria-labelledby="awb-accounts">
          <h2 id="awb-accounts" class="awb-h2 mb-3">{{ $t("airwallexBalance.accounts") }}</h2>
          <div
            v-for="(account, index) in summary.accounts"
            :key="'a' + account.airwallex_account_id"
            class="d-flex justify-content-between gap-3 align-items-start"
            :class="{ 'border-top pt-3 mt-3': index > 0 }"
          >
            <div class="d-flex flex-column" style="min-width: 0">
              <span class="fw-bold">{{ accountName(account) }}</span>
              <span class="small awb-muted text-break">
                {{ account.status === "ACTIVE" ? account.airwallex_account_id : $t("airwallexBalance.notActiveNote") }}
              </span>
            </div>
            <span class="awb-badge" :class="account.status === 'ACTIVE' ? 'awb-badge--ok' : 'awb-badge--pending'">
              {{ $t(account.status === "ACTIVE" ? "airwallexBalance.accountActive" : "airwallexBalance.accountNotActive") }}
            </span>
          </div>
        </section>

        <p class="small awb-muted mb-0">
          {{ syncedAt ? $t("airwallexBalance.updatedAt", { time: syncedAt }) : $t("airwallexBalance.notSyncedYet") }}
        </p>
      </aside>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import VueApexCharts from "vue3-apexcharts";
import PaymentService from "../../../services/payment.service";
import { money, dateTime, dateShort, typeKey, statusKey, statusClass, rowRef } from "./format";

const RECENT_ROWS = 6;

export default {
  name: "balance-overview",
  components: { apexchart: VueApexCharts },
  props: {
    branchId: { type: Number, default: null },
    // Chosen on the page header: period id and its Y-m-d bounds.
    period: { type: String, default: "last30" },
    from: { type: String, required: true },
    to: { type: String, required: true },
  },
  emits: ["accounts", "open", "show-transactions"],
  data() {
    return {
      groupBy: "day",
      loaded: false,
      failed: false,
      summary: null,
      recent: [],
      // Only the latest request may fill the page (period/account switches overlap).
      summaryRequest: 0,
      recentRequest: 0,
      typeKey,
    };
  },
  computed: {
    rangeKey() {
      return this.period + "|" + this.from + "|" + this.to;
    },
    kpis() {
      const totals = this.summary.totals;
      const previous = this.summary.previous;
      const gross = this.change(totals.gross_sales, previous.gross_sales);
      const feePct = totals.gross_sales ? ((totals.fees / totals.gross_sales) * 100).toFixed(1) : "0.0";

      return [
        {
          label: "airwallexBalance.grossSales",
          value: money(totals.gross_sales),
          hint: gross ? this.$t("airwallexBalance.vsPrevious", { change: gross.text }) : this.$t("airwallexBalance.previousAmount", { amount: money(previous.gross_sales) }),
          class: gross ? gross.class : "awb-muted",
        },
        { label: "airwallexBalance.fees", value: money(totals.fees), hint: this.$t("airwallexBalance.feeShare", { pct: feePct }), class: "awb-muted" },
        { label: "airwallexBalance.refunds", value: money(totals.refunds), hint: this.$t("airwallexBalance.previousAmount", { amount: money(previous.refunds) }), class: "awb-muted" },
        { label: "airwallexBalance.paidOut", value: money(totals.payouts), hint: this.$t("airwallexBalance.paidOutHint"), class: "awb-muted" },
      ];
    },
    netChange() {
      return this.change(this.summary.totals.net_earnings, this.summary.previous.net_earnings);
    },
    balanceAccounts() {
      return this.summary.accounts.filter((account) => account.status === "ACTIVE");
    },
    // Sum of the wallets whose balance could be read.
    combined() {
      const read = this.balanceAccounts.filter((account) => account.balance);
      const sum = (pick) => Math.round(read.reduce((total, account) => total + Number(pick(account)), 0) * 100) / 100;
      return {
        total: sum((account) => account.balance.total),
        available: sum((account) => account.balance.available),
        pending: sum((account) => account.balance.pending),
        nextPayout: sum((account) => account.next_payout.amount),
      };
    },
    mismatched() {
      return this.summary.accounts.filter((account) => account.reconciliation && !account.reconciliation.matched);
    },
    syncedAt() {
      // The oldest refresh is how fresh the whole page is.
      const times = this.summary.accounts.map((account) => account.synced_at).filter(Boolean).sort();
      return times.length ? moment.parseZone(times[0]).format("D MMM, HH:mm") : null;
    },
    chartSeries() {
      return [{ name: this.$t("airwallexBalance.netEarnings"), data: this.summary.series.map((point) => point.net_earnings) }];
    },
    chartOptions() {
      const monthly = this.summary.period.group_by === "month";
      const categories = this.summary.series.map((point) =>
        monthly ? moment(point.period, "YYYY-MM").format("MMM YYYY") : moment(point.period, "YYYY-MM-DD").format("D MMM")
      );
      // About 8 labels on the axis; the tooltip still names every bar.
      const step = Math.max(1, Math.ceil(categories.length / 8));
      const shown = new Set(categories.filter((label, index) => index % step === 0));
      return {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: false } },
        colors: ["#5b7bb8"],
        plotOptions: { bar: { columnWidth: monthly ? "45%" : "70%", borderRadius: 3 } },
        dataLabels: { enabled: false },
        grid: { borderColor: "#eceef2" },
        xaxis: {
          categories,
          labels: { rotate: 0, formatter: (label) => (shown.has(label) ? label : ""), style: { colors: "#5f6b85" } },
          tooltip: { enabled: false },
        },
        yaxis: { labels: { formatter: (value) => "S$" + Math.round(value), style: { colors: "#5f6b85" } } },
        tooltip: {
          x: { formatter: (value, { dataPointIndex }) => categories[dataPointIndex] },
          y: {
            formatter: (value, { dataPointIndex }) => {
              const point = this.summary.series[dataPointIndex];
              return money(value) + (point ? " · " + point.payment_count + " " + this.$t("airwallexBalance.payments").toLowerCase() : "");
            },
          },
        },
      };
    },
  },
  watch: {
    branchId() {
      this.loadSummary();
      this.loadRecent();
    },
    // A new period starts on its natural grouping (months for a year).
    period(period) {
      this.groupBy = period === "past12" ? "month" : "day";
    },
    rangeKey() {
      this.loadSummary();
    },
  },
  mounted() {
    this.loadSummary();
    this.loadRecent();
  },
  methods: {
    money,
    dateTime,
    dateShort,
    statusKey,
    statusClass,
    rowRef,
    accountName(account) {
      return account.physical_branch_id
        ? account.branch_name || "#" + account.physical_branch_id
        : this.$t("airwallexBalance.merchantAccount");
    },
    change(current, previous) {
      if (!Number(previous)) return null;
      const pct = ((current - previous) / previous) * 100;
      return { text: (pct >= 0 ? "+" : "−") + Math.abs(pct).toFixed(1) + "%", class: pct >= 0 ? "awb-up" : "awb-down" };
    },
    setGroupBy(groupBy) {
      this.groupBy = groupBy;
      this.loadSummary();
    },
    branchParams() {
      return this.branchId === null ? {} : { physical_branch_id: this.branchId };
    },
    loadSummary() {
      const request = ++this.summaryRequest;
      PaymentService.airwallexBalanceSummary({
        ...this.branchParams(),
        from: this.from,
        to: this.to,
        group_by: this.groupBy,
      }).then((response) => {
        if (request !== this.summaryRequest) return;
        this.failed = !response.data.result.isSuccess;
        if (response.data.result.isSuccess) {
          this.summary = response.data.data;
          this.loaded = true;
          // Only the full list names the accounts for the page's picker.
          if (this.branchId === null) {
            this.$emit("accounts", this.summary.accounts);
          }
        }
      }).catch(() => {
        if (request === this.summaryRequest) this.failed = true;
      });
    },
    // The latest rows whatever the period: reloaded only when the account changes.
    loadRecent() {
      const request = ++this.recentRequest;
      PaymentService.airwallexBalanceTransactions({ ...this.branchParams(), limit: RECENT_ROWS }).then((response) => {
        if (request === this.recentRequest && response.data.result.isSuccess) {
          this.recent = response.data.data.data || [];
        }
      });
    },
  },
};
</script>
